// THE SIGN-OFF TIMELINE (docs SIGNOFF-SPEC S1): the ring timeline on the left, the
// building in the middle, the scope being signed off on the right. Everything is coloured
// for one date - the ring the timeline is scrolled to, a ring clicked, or the scrubber
// below the drawing, whose Play steps one day per second.
//
// THE STAGE (owner 2026-11-05): a GENERATED ISOMETRIC, split by level, area and system,
// the same drawing Project at a Glance makes (SignoffIsometric) - in place of the 3D
// viewer and the tile cubes, which are gone, along with the 3D volume concept. What is
// signed off is a level, an area, or a system inside an area, as it was before volumes;
// one or more systems, in one or more areas, on one or more levels (ScopePicker).
//
// The tile set is still the LEDGER underneath: it is what counts the elements a scope
// covers and what the sign-off records its geometry against (owner 2026-11-01), and it is
// what the isometric's footprints are drawn from. It is simply no longer on screen.
//
// Used by its own page (Coordination > Sign-off timeline) and, `embedded`, as the
// dashboard's tab after At a Glance.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Checkbox, Select, Skeleton, Slider, Tooltip } from "antd";
import { CaretRightOutlined, PauseOutlined, StepBackwardOutlined, StepForwardOutlined } from "@ant-design/icons";
import moment from "moment";
import { Link } from "react-router-dom";
import { downloadExportChanges, downloadSignoffPrint, downloadSignoffSnapshot, getExportChanges, getGlanceManifest, getGridLines, getSignoffTiles, getSignoffTimeline, previewSignoffRequest, signRevision } from "api/signoff";
import addNotification from "helpers/notify";
import { COLORS, GREY, BLUE, GOLD, RING, stateAt, tilesOf, legendCounts } from "./signoffState";
import RingTimeline, { ringColor } from "./RingTimeline";
import SignoffCertificate from "./SignoffCertificate";
import SignoffIsometric, { levelNamesFrom } from "./SignoffIsometric";
import ScopePicker from "./ScopePicker";
import AreaPanel from "./AreaPanel";
import { levelsFrom, scopeTargets } from "./isoModel";
import { gridText } from "./gridSpan";
import RequestSignoffModal from "./RequestSignoffModal";
import OpenRequests from "./OpenRequests";
import "./signoff.scss";

const DAY = 24 * 3600 * 1000;
const SPEEDS = [{ value: 1, label: "1 day / sec" }, { value: 7, label: "1 week / sec" }];
const EMPTY_SCOPE = { levelKeys: [], areaIds: [], systems: [], only: false };

export default function SignoffTimeline({ projectId, embedded = false, onLoaded, requestRef, captureRef }) {
  const [tiles, setTiles] = useState(null);
  const [note, setNote] = useState("");
  const [tl, setTl] = useState(null);
  const [error, setError] = useState("");
  const [at, setAt] = useState(() => new Date());
  const [activeId, setActiveId] = useState("");
  const [outline, setOutline] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [asking, setAsking] = useState(null);
  const [changed, setChanged] = useState({}); // revision ring id -> { tiles: Set of tile keys, uids }
  // THE SCOPE: levels x areas x systems, and the area clicked on the drawing
  const [scope, setScope] = useState(EMPTY_SCOPE);
  const [areaId, setAreaId] = useState("");
  // the fine geometry, when the project has a published Project at a Glance manifest
  const [manifest, setManifest] = useState(null);
  // the structure the drawing worked out (every level, area and system it knows), so the
  // picker offers exactly what is on screen rather than a second, narrower list
  const [structure, setStructure] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewErr, setPreviewErr] = useState("");

  // the page is often left while its two reads are in flight (the dashboard tab, the
  // project picker): a state update then warns about a leak and, worse, puts another
  // project's tiles on screen
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const load = useCallback(async () => {
    if (!projectId) return;
    setError("");
    try {
      const [t, line] = await Promise.all([getSignoffTiles(projectId), getSignoffTimeline(projectId)]);
      if (!alive.current) return;
      setTiles(t.tiles);
      setNote(t.note || "");
      setTl(line);
      if (onLoaded) onLoaded({ tiles: t.tiles, timeline: line });
    } catch (e) {
      if (alive.current) setError(e.message);
    }
  }, [projectId, onLoaded]);
  useEffect(() => { setTiles(null); setTl(null); setAreaId(""); setScope(EMPTY_SCOPE); load(); }, [load]);
  // the building grid, for the grid-line chip
  const [gridLines, setGridLines] = useState(null);
  useEffect(() => {
    let live = true;
    if (projectId) getGridLines(projectId).then((r) => { if (live) setGridLines(r.lines || []); }).catch(() => {});
    return () => { live = false; };
  }, [projectId]);
  // the manifest is the drawing's detail, never its structure: without one the isometric
  // still draws every level and area from the tile set
  useEffect(() => {
    let live = true;
    if (projectId) getGlanceManifest(projectId).then((r) => { if (live) setManifest(r.manifest || null); }).catch(() => { if (live) setManifest(null); });
    return () => { live = false; };
  }, [projectId]);

  const list = useMemo(() => (tiles && tiles.tiles) || [], [tiles]);
  const rings = useMemo(() => {
    const rs = (tl && tl.rings) || [];
    return rs.map((r) => (r.type === "signoff" && r.target ? { ...r, tileCount: tilesOf(list, r.target).length } : r));
  }, [tl, list]);
  const states = useMemo(() => stateAt(rings, at), [rings, at]);
  const [cert, setCert] = useState(null);

  const levelNames = useMemo(() => levelNamesFrom((tl && tl.levels) || {}, manifest), [tl, manifest]);
  const levels = useMemo(() => levelsFrom(list, levelNames), [list, levelNames]);
  const areas = useMemo(() => (tl && tl.areas) || {}, [tl]);
  // the preview is on the same route as asking for a sign-off, so only someone who may ask
  // can price a scope; for everyone else the drawing and the history read, and the scope
  // picker shows what it covers without the counts (a 403 on every click otherwise)
  const canRequest = !!(tl && tl.canRequest);

  // THE SCOPE'S TARGETS, priced by the server: the tiles, elements and activities it covers.
  // The browser never works these out itself - what is shown is what will be recorded.
  const targets = useMemo(
    () => scopeTargets(scope, { areas, levels: (tl && tl.levels) || {} }),
    [scope, areas, tl],
  );
  useEffect(() => {
    if (!targets.length || !canRequest) { setPreview(null); setPreviewErr(""); return undefined; }
    let live = true;
    setPreviewing(true);
    setPreviewErr("");
    const t = setTimeout(() => {
      previewSignoffRequest(projectId, targets)
        .then((p) => { if (live) setPreview(p); })
        .catch((e) => { if (live) { setPreview(null); setPreviewErr(e.message); } })
        .finally(() => { if (live) setPreviewing(false); });
    }, 400);
    return () => { live = false; clearTimeout(t); setPreviewing(false); };
  }, [projectId, targets, canRequest]);

  const toggleArea = useCallback((id) => setScope((s) => ({
    ...s, areaIds: s.areaIds.includes(id) ? s.areaIds.filter((x) => x !== id) : [...s.areaIds, id],
  })), []);
  const toggleLevel = useCallback((key) => setScope((s) => ({
    ...s, levelKeys: s.levelKeys.includes(key) ? s.levelKeys.filter((x) => x !== key) : [...s.levelKeys, key],
  })), []);

  const active = rings.find((r) => r.id === activeId) || null;
  // the ring's tiles: a sign-off's targets, or the tiles an export changed
  const highlight = useMemo(() => {
    if (!outline || !active) return null;
    if (active.type === "export") return [...((changed[active.id] && changed[active.id].tiles) || [])];
    return tilesOf(list, active.target).map((t) => t.key);
  }, [outline, active, list, changed]);
  // the grid lines round what is in focus: the scope, else the ring's tiles, else the model
  const gridKeys = useMemo(() => {
    if (preview && preview.tileKeys && preview.tileKeys.length) return preview.tileKeys;
    if (active && active.type !== "export" && active.target) return tilesOf(list, active.target).map((t) => t.key);
    return list.map((t) => t.key);
  }, [preview, active, list]);
  const gridLabel = tiles ? gridText(gridLines, tiles.grid, gridKeys) : "";
  const [printing, setPrinting] = useState("");
  const print = (day) => {
    setPrinting(day || "all");
    downloadSignoffPrint(projectId, day).catch((e) => addNotification(e.message, "danger")).finally(() => setPrinting(""));
  };
  // a revision: what it changed against the one before - its tiles outlined
  useEffect(() => {
    if (!active || active.type !== "export" || changed[active.id]) return;
    const id = active.id;
    getExportChanges(projectId, id.slice(3))
      .then((c) => setChanged((m) => ({ ...m, [id]: { tiles: new Set((c.tiles || []).map((t) => t.key)), uids: c.uids || [] } })))
      .catch(() => setChanged((m) => ({ ...m, [id]: { tiles: new Set(), uids: [] } })));
  }, [active, changed, projectId]);
  const certKeys = useMemo(() => (cert && cert.target ? tilesOf(list, cert.target).map((t) => t.key) : []), [cert, list]);
  const counts = useMemo(() => legendCounts(list, states), [list, states]);

  // the scrubber's span: the first event to now
  const span = useMemo(() => {
    const times = rings.map((r) => new Date(r.at).getTime());
    const now = Date.now();
    const first = times.length ? Math.min(...times) : now - 30 * DAY;
    return { from: Math.min(first, now - DAY), to: now };
  }, [rings]);

  // A RING CLICKED REPLAYS UP TO IT (owner 2026-11-01): the date steps through every
  // sign-off from the first to the picked one, about 2.5 s at most.
  const replay = useRef(null);
  const stopReplay = () => { if (replay.current) { clearTimeout(replay.current); replay.current = null; } };
  useEffect(() => stopReplay, []);
  const pick = useCallback((r) => {
    stopReplay();
    setActiveId(r.id);
    setPlaying(false);
    const end = new Date(r.at).getTime();
    const stops = [...new Set(rings.filter((x) => x.type === "signoff").map((x) => new Date(x.at).getTime()).filter((t) => t <= end))].sort((a, b) => a - b);
    if (stops.length < 2) { setAt(new Date(r.at)); return; }
    if (stops[stops.length - 1] !== end) stops.push(end);
    const each = Math.max(120, Math.min(450, 2500 / stops.length));
    let i = 0;
    const next = () => {
      setAt(new Date(stops[i]));
      i += 1;
      replay.current = i < stops.length ? setTimeout(next, each) : null;
    };
    next();
  }, [rings]);
  const spy = useCallback((r) => { stopReplay(); setActiveId(r.id); setAt(new Date(r.at)); }, []);

  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setAt((d) => {
        const next = new Date(Math.min(d.getTime() + speed * DAY, span.to));
        if (next.getTime() >= span.to) setPlaying(false);
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [playing, speed, span.to]);
  useEffect(() => {
    if (!playing) return;
    const r = rings.find((x) => new Date(x.at) <= at);
    if (r && r.id !== activeId) setActiveId(r.id);
  }, [at, playing, rings, activeId]);

  const step = (dir) => {
    const sorted = [...rings].sort((a, b) => new Date(a.at) - new Date(b.at));
    const t = at.getTime();
    const r = dir < 0 ? [...sorted].reverse().find((x) => new Date(x.at).getTime() < t - 1000) : sorted.find((x) => new Date(x.at).getTime() > t + 1000);
    if (r) pick(r);
  };

  const openRequest = useCallback((pre) => setAsking({ targets: pre && pre.length ? pre : targets }), [targets]);
  useEffect(() => { if (requestRef) requestRef.current = () => openRequest(null); }, [requestRef, openRequest]);

  // a model revision's two stars, given here or asked for by link
  const rev = tl ? {
    canBlue: !!tl.canRequest, canGold: !!tl.canGold, canRequest: !!tl.canRequest,
    mayWithdraw: (s) => !!tl.canWithdrawAny || (!!s && !!s.byUserId && s.byUserId === tl.me),
    onStar: (r, star, withdraw) => signRevision(projectId, r.versionId, star, withdraw)
      .then(() => { addNotification(withdraw ? "Sign-off withdrawn" : `Revision #${r.number} signed off`, "success"); load(); })
      .catch((e) => addNotification(e.message, "danger")),
    onAsk: (r, star) => setAsking({ targets: [{ kind: "revision", versionId: r.versionId, label: `Model revision #${r.number}${r.exportVersion ? ` (${r.exportVersion})` : ""}` }], grant: star }),
  } : null;

  if (error) return <Alert type="error" showIcon message="The sign-off timeline could not be loaded" description={error} action={<Button onClick={load}>Retry</Button>} />;
  if (!tl || (!tiles && !note)) return <Skeleton active paragraph={{ rows: 10 }} />;

  const sceneH = embedded ? 480 : 620;
  return (
    <div className={`signoff-timeline ${embedded ? "is-embedded" : ""}`}>
      {embedded ? (
        <div className="signoff-embedded-bar">
          <span className="signoff-muted">The building by level, area and system, coloured by owner/GC and contractor sign-off on any date.</span>
          <span className="models-spacer" />
          {tiles ? <span className="signoff-grid-chip">{(structure ? structure.levels.length : levels.length)} levels · {Object.keys(areas).length} areas · {tiles.placed.toLocaleString()} placed elements</span> : null}
          <Link to={`/signoff-timeline/${projectId}`}><Button>Open full page</Button></Link>
          {tl.canRequest ? <Button type="primary" onClick={() => openRequest(null)}>Request sign-off</Button> : null}
        </div>
      ) : null}
      <div className="signoff-grid">
        <RingTimeline rings={rings} activeId={activeId} onPick={pick} onSpy={spy}
                      onPdf={(r) => downloadSignoffSnapshot(projectId, r.id).catch((e) => addNotification(e.message, "danger"))}
                      onChanges={(r) => downloadExportChanges(projectId, r.id.slice(3)).catch((e) => addNotification(e.message, "danger"))} filter={filter} onFilter={setFilter}
                      search={search} onSearch={setSearch} height={sceneH + 40} rev={rev} onCertificate={setCert} />
        <div className="signoff-center">
          <div className="signoff-scene-bar">
            <Tooltip title="Only the levels in the scope are drawn solid; click a level's name on the drawing to add it">
              <Select mode="multiple" allowClear placeholder="Every level" style={{ minWidth: 180 }} maxTagCount="responsive"
                      value={scope.levelKeys} onChange={(v) => setScope((s) => ({ ...s, levelKeys: v }))}
                      options={(structure ? structure.levels : levels).map((l) => ({ value: l.key, label: l.name }))} data-testid="iso-levels" />
            </Tooltip>
            <Tooltip title="Outline the tiles the sign-off selected on the timeline covers">
              <Checkbox checked={outline} onChange={(e) => setOutline(e.target.checked)}>Highlight the ring's area</Checkbox>
            </Tooltip>
            <Tooltip title="The whole sign-off history as a PDF">
              <Button onClick={() => print("")} loading={printing === "all"} data-testid="print-history">Print sign-off history</Button>
            </Tooltip>
            <Tooltip title="A PDF of the sign-offs standing on the date the drawing is showing">
              <Button onClick={() => print(moment(at).format("YYYY-MM-DD"))} loading={!!printing && printing !== "all"} data-testid="print-day">Print {moment(at).format("MMM D, YYYY")}</Button>
            </Tooltip>
            {!manifest ? <span className="signoff-grid-chip is-warn" data-testid="iso-no-manifest">No Project at a Glance drawing yet · the systems cannot be drawn, levels and areas can</span> : null}
          </div>
          <div className="signoff-scene-date">
            <b>{moment(at).format("ddd, MMM D, YYYY · h:mm A")}</b>
            {active ? <span className="signoff-muted"> · {active.type === "export" ? `model export #${active.number}` : active.target.label}</span> : null}
            {gridLabel ? <span className="signoff-grid-chip ml-2" data-testid="timeline-grid">{gridLabel}</span> : null}
          </div>
          {tiles && levels.length ? (
            <SignoffIsometric tiles={tiles} areas={areas} levelNames={levelNames} manifest={manifest} gridLines={gridLines}
                              states={states} scope={scope} onStructure={setStructure}
                              onToggleArea={toggleArea} onToggleLevel={toggleLevel} onPickArea={setAreaId} selectedAreaId={areaId}
                              focusKeys={highlight} height={sceneH} captureRef={captureRef} />
          ) : (
            <Alert className="m-3" type="info" showIcon message="Nothing to draw yet"
                   description={note || "No model export has been placed in drawn levels and areas yet, so there is nothing to draw. The sign-off history below still reads."} />
          )}
          {tiles ? (
            <div className="signoff-legend">
              <div><i style={{ background: COLORS[GREY] }} /> Not signed off <b>{counts[GREY].toLocaleString()}</b></div>
              <div><i style={{ background: COLORS[BLUE] }} /> Owner/GC signed off <span style={{ color: RING.ownerGc }}>★</span> <b>{counts[BLUE].toLocaleString()}</b></div>
              <div><i style={{ background: COLORS[GOLD] }} /> Contractor signed off <span style={{ color: RING.tenant }}>★</span> <b>{counts[GOLD].toLocaleString()}</b></div>
              <small className="signoff-muted">Click an area for its sign-off · shift-click it, or click a level's name, to put it in the scope</small>
              {tiles.elements && tiles.placed < tiles.elements ? <small className="signoff-muted d-block">{Math.round((tiles.placed / tiles.elements) * 100)}% of elements are in a drawn area; the rest are not drawn here</small> : null}
            </div>
          ) : null}
          <div className="signoff-scrubber">
            <Tooltip title={playing ? "Stop replaying" : "Replay the sign-offs, one day a second"}>
              <Button icon={playing ? <PauseOutlined /> : <CaretRightOutlined />} onClick={() => { if (!playing && at.getTime() >= span.to - 60000) setAt(new Date(span.from)); setPlaying(!playing); }}>{playing ? "Pause" : "Play"}</Button>
            </Tooltip>
            <Tooltip title="The event before this date"><Button icon={<StepBackwardOutlined />} onClick={() => step(-1)} aria-label="Previous event" /></Tooltip>
            <Tooltip title="The event after this date"><Button icon={<StepForwardOutlined />} onClick={() => step(1)} aria-label="Next event" /></Tooltip>
            <div className="signoff-track">
              <div className="signoff-ticks">
                {rings.map((r) => (
                  <i key={r.id} title={moment(r.at).format("MMM D")} style={{ left: `${((new Date(r.at).getTime() - span.from) / (span.to - span.from)) * 100}%`, background: ringColor(r) }} />
                ))}
              </div>
              <Slider min={span.from} max={span.to} step={3600 * 1000} value={Math.min(Math.max(at.getTime(), span.from), span.to)}
                      tooltip={{ formatter: (v) => moment(v).format("MMM D, YYYY") }}
                      onChange={(v) => { setPlaying(false); setAt(new Date(v)); }} />
              <div className="signoff-track-ends"><span>{moment(span.from).format("MMM D")}</span><span>Today</span></div>
            </div>
            <Tooltip title="How fast Play steps through the days"><Select value={speed} onChange={setSpeed} options={SPEEDS} style={{ width: 120 }} /></Tooltip>
          </div>
        </div>
        <div className="signoff-side">
          <ScopePicker projectId={projectId} structure={structure} levelNames={(tl && tl.levels) || {}} areas={areas} tiles={tiles}
                       manifest={manifest} scope={scope} setScope={setScope}
                       preview={preview} loading={previewing} err={previewErr}
                       canRequest={canRequest} onRequest={(ts) => setAsking({ targets: ts })} />
          {areaId ? (
            <AreaPanel areaId={areaId} areas={areas} tiles={tiles} states={states} rings={rings} at={at}
                       inScope={scope.areaIds.includes(areaId)} onScope={toggleArea}
                       canRequest={canRequest} onRequest={(id) => setAsking({ targets: [{ kind: "area", areaSetId: id }] })} />
          ) : null}
        </div>
      </div>
      {cert ? (
        <SignoffCertificate projectId={projectId} projectTitle={tl.projectTitle} ring={cert} tiles={tiles} coveredKeys={certKeys}
                            areas={areas} levels={tl.levels} rings={rings} gridLines={gridLines}
                            onPdf={(r) => downloadSignoffSnapshot(projectId, r.id).catch((e) => addNotification(e.message, "danger"))} onClose={() => setCert(null)} />
      ) : null}
      {asking ? (
        <RequestSignoffModal projectId={projectId} areas={areas} levels={tl.levels} tiles={list} initial={asking.targets} defaultGrant={asking.grant || "ownerGc"}
                             onClose={() => setAsking(null)} onSent={() => { setAsking(null); load(); }} />
      ) : null}
      <OpenRequests projectId={projectId} requests={tl.requests} canRequest={tl.canRequest} onChanged={load} />
    </div>
  );
}
